"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreamMarkers = void 0;
const react_google_maps_1 = require("@vis.gl/react-google-maps");
const react_1 = __importStar(require("react"));
const colors_1 = require("../../../../assets/styles/colors");
const hooks_1 = require("../../../../store/hooks");
const index_1 = __importDefault(require("../../../../store/index"));
const mapSlice_1 = require("../../../../store/mapSlice");
const markersLoadingSlice_1 = require("../../../../store/markersLoadingSlice");
const mobileStreamSelectors_1 = require("../../../../store/mobileStreamSelectors");
const thresholdSlice_1 = require("../../../../store/thresholdSlice");
const thresholdColors_1 = require("../../../../utils/thresholdColors");
const CustomMarker_1 = require("./CustomOverlays/CustomMarker");
const HoverMarker_1 = __importDefault(require("./HoverMarker/HoverMarker"));
const StreamMarkers = ({ sessions, unitSymbol }) => {
    const dispatch = (0, hooks_1.useAppDispatch)();
    const map = (0, react_google_maps_1.useMap)();
    const markersRef = (0, react_1.useRef)(new Map());
    const thresholds = (0, hooks_1.useAppSelector)(thresholdSlice_1.selectThresholds);
    const polylineRef = (0, react_1.useRef)(null);
    const hoverPosition = (0, hooks_1.useAppSelector)(mapSlice_1.selectHoverPosition);
    const mobileStreamData = (0, hooks_1.useAppSelector)(mobileStreamSelectors_1.selectMobileStreamData);
    const [CustomOverlay, setCustomOverlay] = (0, react_1.useState)(null);
    const isInitialLoading = (0, react_1.useRef)(true);
    const ZOOM_FOR_SELECTED_SESSION = 16;
    const LAT_DIFF_SMALL = 0.00001;
    const LAT_DIFF_MEDIUM = 0.0001;
    const LAT_ADJUST_SMALL = 0.005;
    const sortedSessions = (0, react_1.useMemo)(() => {
        const validSessions = sessions.filter((session) => session.point &&
            typeof session.point.lat === "number" &&
            typeof session.point.lng === "number");
        const locationMap = new Map();
        validSessions.forEach((session) => {
            const locationKey = `${session.point.lat},${session.point.lng}`;
            const existingSession = locationMap.get(locationKey);
            if (!existingSession ||
                (session.time &&
                    existingSession.time &&
                    new Date(session.time.toString()) >
                        new Date(existingSession.time.toString()))) {
                locationMap.set(locationKey, session);
            }
        });
        const sorted = Array.from(locationMap.values()).sort((a, b) => {
            const timeA = a.time ? new Date(a.time.toString()).getTime() : 0;
            const timeB = b.time ? new Date(b.time.toString()).getTime() : 0;
            return timeA - timeB;
        });
        return sorted;
    }, [sessions]);
    const handleIdle = (0, react_1.useCallback)(() => {
        dispatch((0, markersLoadingSlice_1.setMarkersLoading)(false));
    }, [dispatch]);
    /**
     * Creates or updates a marker for a given session
     * @param session - The mobile session to create a marker for
     * @param map - The Google Maps instance
     * @param CustomOverlay - The custom marker overlay class
     * @param thresholds - The threshold values for coloring
     * @param unitSymbol - The unit symbol to display
     * @param markersRef - Reference to the markers map
     * @returns The created or updated marker
     */
    const createOrUpdateMarker = (session, map, CustomOverlay, thresholds, unitSymbol, markersRef) => {
        const markerId = session.id.toString();
        const position = { lat: session.point.lat, lng: session.point.lng };
        const title = `${session.lastMeasurementValue} ${unitSymbol}`;
        let marker = markersRef.current.get(markerId);
        if (!marker) {
            const color = (0, thresholdColors_1.getColorForValue)(thresholds, session.lastMeasurementValue);
            marker = new CustomOverlay(position, color, title, 12, 20, "overlayMouseTarget", []);
            marker.setMap(map);
            markersRef.current.set(markerId, marker);
        }
        else {
            marker.setPosition(position);
            marker.setTitle(title);
            marker.setNotes([]);
            if (marker.getMap() !== map) {
                marker.setMap(map);
            }
        }
        return marker;
    };
    (0, react_1.useEffect)(() => {
        if (window.google && window.google.maps && !CustomOverlay) {
            setCustomOverlay(() => (0, CustomMarker_1.getCustomMarkerClass)());
        }
    }, [CustomOverlay]);
    const centerMapOnBounds = (0, react_1.useCallback)((bounds) => {
        if (!map)
            return;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latDiff = ne.lat() - sw.lat();
        const lngDiff = ne.lng() - sw.lng();
        if (latDiff < LAT_DIFF_SMALL && lngDiff < LAT_DIFF_SMALL) {
            const centerLat = (ne.lat() + sw.lat()) / 2;
            const centerLng = (ne.lng() + sw.lng()) / 2;
            map.setCenter({ lat: centerLat, lng: centerLng });
            map.setZoom(ZOOM_FOR_SELECTED_SESSION);
        }
        else {
            let adjustedLat;
            if (latDiff >= 0 && latDiff < LAT_DIFF_SMALL) {
                adjustedLat = sw.lat() - LAT_ADJUST_SMALL;
            }
            else if (latDiff >= LAT_DIFF_SMALL && latDiff < LAT_DIFF_MEDIUM) {
                adjustedLat = sw.lat() - latDiff * 2;
            }
            else {
                adjustedLat = sw.lat() - latDiff;
            }
            const adjustedBounds = new google.maps.LatLngBounds(new google.maps.LatLng(adjustedLat, sw.lng()), new google.maps.LatLng(ne.lat(), ne.lng()));
            map.fitBounds(adjustedBounds);
            if (latDiff === 0) {
                map.setZoom(ZOOM_FOR_SELECTED_SESSION);
            }
        }
    }, [map]);
    (0, react_1.useEffect)(() => {
        if (!map || !CustomOverlay)
            return;
        const cleanup = () => {
            markersRef.current.forEach((marker) => {
                marker.setMap(null);
                marker.cleanup();
            });
            markersRef.current.clear();
            if (noteMarkersRef.current) {
                noteMarkersRef.current.forEach((marker) => {
                    marker.setMap(null);
                    marker.cleanup();
                });
                noteMarkersRef.current.clear();
            }
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
            }
        };
        cleanup();
        dispatch((0, markersLoadingSlice_1.setMarkersLoading)(true));
        isInitialLoading.current = true;
        if (sessions.length === 0) {
            dispatch((0, markersLoadingSlice_1.setMarkersLoading)(false));
            return cleanup;
        }
        if (sortedSessions.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            sortedSessions.forEach((session) => {
                bounds.extend({ lat: session.point.lat, lng: session.point.lng });
            });
            centerMapOnBounds(bounds);
        }
        requestAnimationFrame(() => {
            const allNotesForStream = mobileStreamData.notes || [];
            const markerLocations = new Set();
            // Create markers for sessions
            sortedSessions.forEach((session) => {
                createOrUpdateMarker(session, map, CustomOverlay, thresholds, unitSymbol, markersRef);
                markerLocations.add(`${session.point.lat},${session.point.lng}`);
            });
            // Create invisible markers for each note location, each with all notes for the session
            if (!noteMarkersRef.current)
                noteMarkersRef.current = new Map();
            // Group notes by location
            const notesByLocation = new Map();
            allNotesForStream.forEach((note) => {
                var _a;
                const key = `${note.latitude},${note.longitude}`;
                if (!notesByLocation.has(key)) {
                    notesByLocation.set(key, []);
                }
                (_a = notesByLocation.get(key)) === null || _a === void 0 ? void 0 : _a.push(note);
            });
            // Sort all notes by date for consistent navigation order
            const allNotesSorted = [...allNotesForStream].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            // Create markers at each note location with only the notes at that location
            notesByLocation.forEach((locationNotes, locationKey) => {
                const [lat, lng] = locationKey.split(",").map(Number);
                const noteMarkerId = `note-${locationKey}`;
                let noteMarker = noteMarkersRef.current.get(noteMarkerId);
                const initialSlideInGlobal = allNotesSorted.findIndex((note) => note.latitude === lat && note.longitude === lng);
                if (!noteMarker) {
                    noteMarker = new CustomOverlay({ lat, lng }, "", // no color needed
                    "", // no title needed
                    12, // size
                    20, // clickable area size
                    "overlayMouseTarget", // pane name
                    allNotesSorted, // notes array
                    undefined, // content
                    undefined, // onClick
                    false // invisible
                    );
                    // Set initial slide separately since we can't pass it in the constructor
                    if (noteMarker) {
                        noteMarker.setNotes(allNotesSorted, initialSlideInGlobal);
                    }
                    noteMarker.setMap(map);
                    noteMarkersRef.current.set(noteMarkerId, noteMarker);
                }
                else {
                    noteMarker.setPosition({ lat, lng });
                    noteMarker.setNotes(allNotesSorted, initialSlideInGlobal);
                    if (noteMarker.getMap() !== map) {
                        noteMarker.setMap(map);
                    }
                }
            });
            // Cleanup stale markers
            noteMarkersRef.current.forEach((marker, markerId) => {
                if (markerId.startsWith("note-")) {
                    const locationKey = markerId.replace("note-", "");
                    if (!notesByLocation.has(locationKey)) {
                        marker.setMap(null);
                        marker.cleanup();
                        noteMarkersRef.current.delete(markerId);
                    }
                }
            });
            const path = sortedSessions.map((session) => ({
                lat: session.point.lat,
                lng: session.point.lng,
            }));
            if (polylineRef.current) {
                polylineRef.current.setPath(path);
            }
            else {
                polylineRef.current = new google.maps.Polyline({
                    path,
                    map,
                    strokeColor: colors_1.mobileStreamPath,
                    strokeOpacity: 0.7,
                    strokeWeight: 4,
                });
            }
            markersRef.current.forEach((marker, markerId) => {
                if (!sortedSessions.some((session) => session.id.toString() === markerId)) {
                    marker.setMap(null);
                    markersRef.current.delete(markerId);
                }
            });
            dispatch((0, markersLoadingSlice_1.setTotalMarkers)(sortedSessions.length));
            dispatch((0, markersLoadingSlice_1.setMarkersLoading)(false));
            isInitialLoading.current = false;
            dispatch((0, mapSlice_1.setHoverPosition)(null));
        });
        return cleanup;
    }, [
        map,
        sortedSessions,
        sessions,
        dispatch,
        handleIdle,
        CustomOverlay,
        centerMapOnBounds,
        mobileStreamData.notes,
    ]);
    (0, react_1.useEffect)(() => {
        markersRef.current.forEach((marker, markerId) => {
            const session = sortedSessions.find((s) => s.id.toString() === markerId);
            if (session) {
                const newColor = (0, thresholdColors_1.getColorForValue)(thresholds, session.lastMeasurementValue);
                marker.setColor(newColor);
            }
        });
    }, [thresholds, sortedSessions]);
    const noteMarkersRef = react_1.default.useRef(new Map());
    // Add Redux subscription to update all marker popovers
    react_1.default.useEffect(() => {
        let lastOpenMarkerKey = null;
        let lastInitialSlide = null;
        const unsubscribe = index_1.default.subscribe(() => {
            const state = index_1.default.getState();
            const openMarkerKey = state.popover.openMarkerKey;
            const initialSlide = state.popover.initialSlide;
            if (openMarkerKey !== lastOpenMarkerKey ||
                initialSlide !== lastInitialSlide) {
                lastOpenMarkerKey = openMarkerKey;
                lastInitialSlide = initialSlide;
                if (map && map.__customMarkers) {
                    map.__customMarkers.forEach((marker) => {
                        marker.setNotes(marker.getNotes(), initialSlide);
                    });
                }
            }
        });
        return () => unsubscribe();
    }, [map]);
    return !isInitialLoading.current && hoverPosition ? (react_1.default.createElement(HoverMarker_1.default, { position: hoverPosition })) : null;
};
exports.StreamMarkers = StreamMarkers;
